import { Checkbox } from 'antd';
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { SearchIconStroke } from "../../../../../assets/icons/common/common-icons";
import { BaseModal } from "../../../../../common/components/BaseModal/BaseModal";
import { CommonBadge } from "../../../../../common/components/CommonBadge/CommonBadge";
import { ItemLogo } from "../../../../../common/components/ItemLogo/ItemLogo";
import { colors } from '../../../../../common/styles/theme.styles';
import { createAssetSearch } from "../../../../../common/utils/fuzzy-search.utils";
import { useStore } from "../../../../app/data/root.store";
import { getAllLinkableItems } from "../../../utils/linkable-items.utils";
import {
    AssetItem,
    AssetItemCategory,
    AssetItemContent,
    AssetItemDetails,
    AssetItemName,
    BulkLinkedItemsEditorContainer,
    CheckboxWrapper,
    EmptyState,
    ItemsListContainer,
    ItemsSection,
    SearchIconWrapper,
    SearchInput,
    SearchInputWrapper,
    SearchSection
} from './LinkedItemsEditModal.styles';
import { VaultActionButton, VaultModalContent, VaultModalFooter, VaultModalHeader, VaultWarningMessage } from './SharedModalComponents';

const BulkLinkedItemsEditor = ({ selectedItems, onItemsChange }) => {
    const { metadataStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState(new Set(selectedItems.map(item => item.id)));
    const [initiallySelectedIds] = useState(new Set(selectedItems.map(item => item.id)));

    useEffect(() => {
        const newSelectedIds = new Set(selectedItems.map(item => item.id));
        setSelectedAssetIds(newSelectedIds);
    }, [selectedItems]);

    useEffect(() => {
        if (!metadataStore.isLoaded && !metadataStore.isLoading) {
            metadataStore.fetchData();
        }
    }, [metadataStore]);

    const allAssets = useMemo(() => {
        return getAllLinkableItems(metadataStore, initiallySelectedIds);
    }, [metadataStore, initiallySelectedIds]);

    const filteredAssets = useMemo(() => {
        if (!allAssets.length) return [];
        
        const fuzzySearch = createAssetSearch(allAssets);
        const searchResults = fuzzySearch(searchTerm);
        
        // Sort so that initially selected items appear at the top
        return searchResults.sort((a, b) => {
            if (a.wasSelected && !b.wasSelected) return -1;
            if (!a.wasSelected && b.wasSelected) return 1;
            return 0;
        });
    }, [allAssets, searchTerm]);

    const handleItemToggle = (assetId) => {
        const newSelectedIds = new Set(selectedAssetIds);
        if (newSelectedIds.has(assetId)) {
            newSelectedIds.delete(assetId);
        } else {
            newSelectedIds.add(assetId);
        }
        setSelectedAssetIds(newSelectedIds);
        
        // Update the parent component
        const selectedAssets = allAssets.filter(asset => newSelectedIds.has(asset.id));
        onItemsChange(selectedAssets);
    };

    const renderAssetItem = (asset) => {
        const isSelected = selectedAssetIds.has(asset.id);
        const displayName = asset.title || asset.name || 'Unnamed Asset';

        return (
            <AssetItem
                key={asset.id}
                onClick={() => handleItemToggle(asset.id)}
                isSelected={isSelected}
            >
                <AssetItemContent>
                    <ItemLogo item={asset} customStyles={{ marginRight: '0px' }} />
                    <AssetItemDetails>
                        <AssetItemName>
                            {displayName}
                            {asset.isClosed && <CommonBadge badgeType="closed" noMargin />}
                            {asset.connectedProvider === 'vip' && <CommonBadge badgeType="vipService" noMargin />}
                            {asset.ownershipPercentage && (
                                <CommonBadge
                                    title={`${asset.ownershipPercentage}%`}
                                    badgeWrapperStyle={{
                                        background: '#DDE3EB',
                                        color: '#585D66',
                                        borderRadius: '2px 0px 0px 2px',
                                        padding: '1px 1px 0px 4px',
                                        height: '16px'
                                    }}
                                    noMargin
                                />
                            )}
                        </AssetItemName>
                        <AssetItemCategory>
                            {asset.category?.title || 'Unknown'}
                        </AssetItemCategory>
                    </AssetItemDetails>
                </AssetItemContent>
                <CheckboxWrapper>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => handleItemToggle(asset.id)}
                        onClick={(event) => event.stopPropagation()}
                    />
                </CheckboxWrapper>
            </AssetItem>
        );
    };

    return (
        <BulkLinkedItemsEditorContainer>
            <SearchSection>
                <SearchInputWrapper>
                    <SearchInput
                        type="text"
                        placeholder="Search asset / liability"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <SearchIconWrapper>
                        <SearchIconStroke />
                    </SearchIconWrapper>
                </SearchInputWrapper>
            </SearchSection>

            <ItemsSection>
                <ItemsListContainer>
                    {filteredAssets.length === 0 ? (
                        <EmptyState>
                            {searchTerm ? 'No assets found matching your search.' : 'No assets available.'}
                        </EmptyState>
                    ) : (
                        filteredAssets.map(renderAssetItem)
                    )}
                </ItemsListContainer>
            </ItemsSection>
        </BulkLinkedItemsEditorContainer>
    );
};

export const LinkedItemsEditModal = observer(({ onClose, onSave }) => {
    const { vaultStore } = useStore();
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        if (vaultStore.selectedFilesForBulkEdit.length > 0) {
            const commonItems = vaultStore.commonLinkedItemsAcrossSelectedFiles;
            setSelectedItems(commonItems);
        }
    }, [vaultStore.selectedFilesForBulkEdit, vaultStore.commonLinkedItemsAcrossSelectedFiles]);

    const handleSave = () => {
        const assetIds = selectedItems
            .map(item => item.id)
            .filter(id => id && typeof id === 'number');

        onSave(assetIds);
    };

    const getModalTitle = () => {
        const count = vaultStore.selectedFilesForBulkEdit.length;
        return `Edit selected item${count === 1 ? '' : 's'} (${count})`;
    };

        return (
        <BaseModal width="700px" height="auto" borderRadius={12} onBackDropClick={onClose}>
            <VaultModalHeader onClose={onClose}>
                {getModalTitle()}
            </VaultModalHeader>
            
            <VaultModalContent>
                <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    color: '#1A1B1D',
                    fontFamily: 'Circular',
                    lineHeight: '1.2649999346051897em'
                }}>
                    Edit linked items:
                </h4>
                <BulkLinkedItemsEditor
                    selectedItems={selectedItems}
                    onItemsChange={setSelectedItems}
                />
                
                <VaultWarningMessage>
                    This action will replace all existing linked items with the selected items for all selected files.
                </VaultWarningMessage>
            </VaultModalContent>

            <VaultModalFooter>
                <VaultActionButton 
                    onClick={handleSave} 
                    backgroundColor={colors.darkGray1} 
                    whiteText
                >
                    Save
                </VaultActionButton>
            </VaultModalFooter>
        </BaseModal>
    );
}); 